"use client"

import { t } from "@/languages/i18n"
import { WebsiteResponseDataObject } from "@/types/codegen/data-contracts"
import { Checkbox, Divider, List } from "antd"
import React, { useEffect, useMemo, useState } from "react"
import { fetchWebsites } from "../services/post"

interface WebsitesProps {
  selectedWebsites: number[]
  onChangeWebsites: (checkedValues: number[]) => void
}

/**
 * Website list — redesigned: no own card / collapse. Parent FormSection groups it.
 * Adds a "Chọn tất cả" master checkbox that toggles every website at once.
 */
const Websites: React.FC<WebsitesProps> = ({ selectedWebsites, onChangeWebsites }) => {
  const [websites, setWebsites] = useState<WebsiteResponseDataObject[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(false)

  const handleFetchWebsites = async () => {
    try {
      setIsLoading(true)
      const response = await fetchWebsites()
      setWebsites(response.data)
    } catch {
      console.error(t("Error fetching websites"))
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    handleFetchWebsites()
  }, [])

  const allIds = useMemo(
    () => websites.map(item => item.id).filter((id): id is number => id != null),
    [websites],
  )
  const checkedCount = useMemo(
    () => allIds.filter(id => selectedWebsites.includes(id)).length,
    [allIds, selectedWebsites],
  )
  const isAllChecked = allIds.length > 0 && checkedCount === allIds.length
  const isIndeterminate = checkedCount > 0 && checkedCount < allIds.length

  const handleToggleAll = (e: { target: { checked: boolean } }) => {
    onChangeWebsites(e.target.checked ? [...allIds] : [])
  }

  return (
    <div className="w-full">
      {/* Master "select all" lives OUTSIDE Checkbox.Group — a grouped Checkbox
          has its `checked` overridden by the group value, so it can't self-manage. */}
      {allIds.length > 0 && (
        <>
          <Checkbox
            indeterminate={isIndeterminate}
            checked={isAllChecked}
            onChange={handleToggleAll}
          >
            {t("Chọn tất cả")}
          </Checkbox>
          <Divider style={{ margin: "8px 0" }} />
        </>
      )}
      <Checkbox.Group onChange={onChangeWebsites} value={selectedWebsites} className="w-full">
        <List
          loading={isLoading}
          className="w-full"
          size="small"
          dataSource={websites}
          split={false}
          renderItem={item => (
            <List.Item style={{ padding: "4px 0", borderBottom: "none" }}>
              <Checkbox key={item.id} value={item.id}>
                {item?.attributes?.name || item?.attributes?.domain}
              </Checkbox>
            </List.Item>
          )}
        />
      </Checkbox.Group>
    </div>
  )
}

export default Websites
